/* New Arrivals shelf (LSC) — entrance + hover motion + scroller controls.
   House rules honored here:
   - GSAP/ScrollTrigger come from theme.liquid (never loaded twice).
   - Entrance is skipped (content stays visible) under reduced motion, in the theme
     editor, when GSAP is missing, or when the tab loads in the background — the
     wall-clock/rAF guards that bit us before are replaced by "just don't arm".
   - Anything that DOES get armed hidden has two independent backstops (IntersectionObserver
     + hard timer) so the shelf can never ship blank if a reveal misses.
   - Scroller arrows work with no GSAP at all. */
(function () {
  'use strict';

  function initShelf(root) {
    if (root.dataset.nvsReady) return;
    root.dataset.nvsReady = '1';

    var viewport = root.querySelector('[data-nvs-viewport]');
    var track = root.querySelector('[data-nvs-track]');
    var items = track ? [].slice.call(track.querySelectorAll('[data-nvs-item]')) : [];
    if (!viewport || !track || !items.length) return;

    /* ---------- scroller controls (GSAP-independent) ---------- */
    var prev = root.querySelector('[data-nvs-prev]');
    var next = root.querySelector('[data-nvs-next]');

    function updateOverflow() {
      var overflowing = track.scrollWidth > track.clientWidth + 4;
      root.classList.toggle('nvs--overflow', overflowing);
      updateArrows();
    }
    function updateArrows() {
      var max = track.scrollWidth - track.clientWidth;
      var atStart = track.scrollLeft <= 2;
      var atEnd = track.scrollLeft >= max - 2;
      root.classList.toggle('nvs--fade-l', !atStart);
      root.classList.toggle('nvs--fade-r', !atEnd);
      if (!prev || !next) return;
      prev.disabled = atStart;
      next.disabled = atEnd;
    }
    function nudge(dir) {
      track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });

    var scrollRaf = 0;
    track.addEventListener('scroll', function () {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () { scrollRaf = 0; updateArrows(); });
    }, { passive: true });
    if ('ResizeObserver' in window) {
      new ResizeObserver(updateOverflow).observe(track);
    }
    updateOverflow();

    /* ---------- motion ---------- */
    var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    var design = !!(window.Shopify && window.Shopify.designMode);
    var hasGsap = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
    if (!hasGsap || reduce) return;
    try { gsap.registerPlugin(ScrollTrigger); } catch (e) { return; }

    /* entrance — only when it will actually be seen */
    if (!design && document.visibilityState === 'visible') {
      var revealed = false;
      function settle() {
        if (revealed) return;
        revealed = true;
        gsap.set(items, { clearProps: 'transform,opacity,visibility' });
      }

      gsap.set(items, { autoAlpha: 0, y: 26, scale: 0.96 });
      var tween = gsap.to(items, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.65,
        ease: 'back.out(1.4)',
        stagger: { each: 0.055, from: 'start' },
        paused: true,
        onComplete: settle,
      });
      var trigger = ScrollTrigger.create({
        trigger: viewport,
        start: 'clamp(top 88%)',
        once: true,
        onEnter: function () { if (!revealed) tween.play(); },
      });

      function forceReveal() {
        if (revealed) return;
        try { trigger.kill(); tween.kill(); } catch (e) { /* settle regardless */ }
        settle();
      }
      /* backstop 1: card entered the viewport but is still hidden shortly after */
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            io.disconnect();
            setTimeout(function () {
              if (revealed || tween.isActive() || tween.progress() > 0) return;
              forceReveal();
            }, 500);
          });
        }, { threshold: 0.05 });
        io.observe(viewport);
      }
      /* backstop 2: hard ceiling */
      setTimeout(function () {
        if (!revealed && !tween.isActive() && tween.progress() === 0) {
          var r = viewport.getBoundingClientRect();
          if (r.top < innerHeight && r.bottom > 0) forceReveal();
        }
      }, 3500);
      /* bfcache restore: never come back mid-arm */
      window.addEventListener('pageshow', function (e) {
        if (e.persisted) forceReveal();
      });
    }

    /* hover lift — desktop pointers only, cleaned up by matchMedia */
    var mm = gsap.matchMedia();
    mm.add('(pointer: fine)', function () {
      var handlers = items.map(function (item) {
        var card = item.querySelector('.nvs__card');
        if (!card) return null;
        var liftY = gsap.quickTo(card, 'y', { duration: 0.35, ease: 'power3.out' });
        var liftS = gsap.quickTo(card, 'scale', { duration: 0.35, ease: 'power3.out' });
        function enter() { liftY(-6); liftS(1.028); }
        function leave() { liftY(0); liftS(1); }
        function down() { liftS(0.975); }
        function up() { liftS(1.028); }
        card.addEventListener('pointerenter', enter);
        card.addEventListener('pointerleave', leave);
        card.addEventListener('pointerdown', down);
        card.addEventListener('pointerup', up);
        return function () {
          card.removeEventListener('pointerenter', enter);
          card.removeEventListener('pointerleave', leave);
          card.removeEventListener('pointerdown', down);
          card.removeEventListener('pointerup', up);
          gsap.set(card, { clearProps: 'transform' });
        };
      });
      return function () {
        handlers.forEach(function (off) { if (off) off(); });
      };
    });
  }

  function initAll(scope) {
    [].slice.call((scope || document).querySelectorAll('[data-nvs]')).forEach(initShelf);
  }

  /* deferred scripts can execute at readyState "interactive" BEFORE later deferred
     scripts have run — only trust immediate init once fully complete */
  if (document.readyState === 'complete') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  /* theme editor: re-init when the merchant reloads/moves the section */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-nvs]')) initAll(e.target);
  });
})();
